import React, { useEffect, useState, useRef } from 'react';
import Header from './Header.jsx';
import Footer from './Footer.jsx';
import InquiryBanner from './InquiryBanner.jsx';
import banner from '../assets/gatevalves/banner.jpg';
import { FiSettings, FiCheckCircle, FiLayers, FiShield, FiTrendingUp, FiAward, FiArrowRight } from 'react-icons/fi';
import p2 from '../assets/gatevalves/gatevalves.PNG';
import valvesMatrix from '../assets/gatevalves/valves_matrix.png';
import picture1 from '../assets/gatevalves/gtv1.PNG';
import picture2 from '../assets/gatevalves/gtv_2.PNG';
import '../css/gateValves.css';

const valveTypes = [
  {
    name: 'Rising Stem Gate Valves',
    description: 'Gate valves with rising stem design providing visual indication of valve position and easier operation.',
    features: ['Visual position indication', 'Easy maintenance', 'Suitable for above-ground installation', 'Standard stem design']
  },
  {
    name: 'Non-Rising Stem Gate Valves',
    description: 'Compact gate valves ideal for space-constrained installations where stem height is a concern.',
    features: ['Compact design', 'Space-efficient', 'Below-ground installation', 'No stem height requirement']
  },
  {
    name: 'Bolted Bonnet Gate Valves',
    description: 'Gate valves with bolted bonnet design ensuring secure sealing and easy access for maintenance.',
    features: ['Secure bonnet attachment', 'Easy maintenance access', 'High-pressure capability', 'Reliable sealing']
  },
  {
    name: 'Cryogenic Gate Valves',
    description: 'Specialized gate valves designed for extremely low-temperature applications in cryogenic systems.',
    features: ['Low-temperature operation', 'Special material selection', 'Extended bonnet design', 'Cryogenic sealing']
  }
];

const specifications = [
  {
    title: 'Size Range',
    value: '2" – 24"',
    icon: <FiSettings />
  },
  {
    title: 'Pressure Rating',
    value: 'Up to Class 2500',
    icon: <FiTrendingUp />
  },
  {
    title: 'Materials',
    value: 'Carbon Steel, Stainless Steel, Alloy, Duplex',
    icon: <FiLayers />
  },
  {
    title: 'Standards',
    value: 'ASME B16.10, ASME B16.34, API 600',
    icon: <FiAward />
  }
];

const advantages = [
  {
    icon: <FiSettings />,
    title: 'Optimal Flow Control',
    description: 'Full-bore design ensures minimal pressure drop and unrestricted flow, making gate valves ideal for on/off applications where flow efficiency is critical.'
  },
  {
    icon: <FiShield />,
    title: 'Reliable Sealing',
    description: 'Metal-to-metal sealing provides excellent leak-tight performance, suitable for high-pressure applications and critical service conditions.'
  },
  {
    icon: <FiAward />,
    title: 'Industry Standards',
    description: 'Manufactured to ASME B16.10, ASME B16.34, and API 600 standards, ensuring consistent quality and compliance with international specifications.'
  },
  {
    icon: <FiTrendingUp />,
    title: 'High-Pressure Capability',
    description: 'Designed to handle extreme pressure ratings up to Class 2500, suitable for the most demanding industrial applications in refineries and power plants.'
  }
];

const applications = [
  {
    title: 'Oil & Gas Refineries',
    description: 'Critical applications in refineries and petrochemical plants for isolation and flow control of hydrocarbons and process fluids.'
  },
  {
    title: 'Power Generation',
    description: 'Steam and water systems in power plants requiring reliable isolation valves for maintenance and emergency shutdown operations.'
  },
  {
    title: 'Chemical Processing',
    description: 'Chemical plants handling corrosive and hazardous materials where bubble-tight sealing and reliability are essential.'
  },
  {
    title: 'Water & Wastewater',
    description: 'Municipal water systems and wastewater treatment plants requiring durable valves for flow isolation and control.'
  },
  {
    title: 'Marine & Offshore',
    description: 'Marine applications and offshore platforms where corrosion resistance and reliability are critical in harsh environments.'
  },
  {
    title: 'Mining Industry',
    description: 'Mining operations requiring robust valves for abrasive slurry handling and high-pressure applications in processing plants.'
  }
];

const features = [
  'Rising/Non-rising stem options',
  'Bolted Bonnet design',
  'Cryogenic options available',
  'Metal-to-metal sealing',
  'Fire-safe design options',
  'Full-bore design',
  'Suitable for high-pressure applications',
  'Multiple material options'
];

const GateValves = () => {
  const sectionsRef = useRef([]);
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('scroll-visible');
          observer.unobserve(entry.target);
        }
      });
    }, observerOptions);

    // Use setTimeout to ensure refs are set after render
    const timeoutId = setTimeout(() => {
    sectionsRef.current.forEach((section) => {
      if (section) {
        observer.observe(section);
      }
    });
      // Make first section visible immediately
      if (sectionsRef.current[0]) {
        sectionsRef.current[0].classList.add('scroll-visible');
      }
    }, 100);

    return () => {
      clearTimeout(timeoutId);
      sectionsRef.current.forEach((section) => {
        if (section) {
          observer.unobserve(section);
        }
      });
    };
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <>
      <Header />
      <div className="gate-valves-banner">
        <img
          src={banner}
          alt="Gate Valves Banner"
          className="gate-valves-banner__img"
        />
        <div className="gate-valves-banner__overlay"></div>
        <div className="gate-valves-banner__content">
          <h2 className="gate-valves-banner__title">Gate Valves</h2>
          <div className="gate-valves-banner__subtitle">
            High-performance gate valves designed for optimal flow control with minimal pressure drop
          </div>
        </div>
      </div>

      <div 
        ref={(el) => (sectionsRef.current[0] = el)}
        className="scroll-section initial-visible"
      >
        <section className="gate-valves-intro">
          <div className="container">
            <div className="gv-intro-content">
              <div className="gv-intro-image">
                <img src={p2} alt="Gate Valves" />
              </div>
              <div className="gv-intro-description">
                <h2>Gate Valves</h2>
                <p>
                  Gate Valves that opens by lifting a wedge out of the path of the fluid. The distinct 
                  feature of a gate piping component is the sealing surfaces between the gate and 
                  seats are planar. The gate faces can form a wedge shape or they can be parallel. 
                  Typical gate piping components should never be used for regulating flow, unless they 
                  are specifically designed for that purpose. On opening the gate piping component, 
                  the flow path is enlarged in a highly nonlinear manner with respect to the percentage 
                  of opening.
                </p>
              </div>
            </div>
            <div className="gv-specs-row">
              <div className="gv-spec-card-horizontal">
                <h3>Product Range</h3>
                <ul>
                  <li><strong>Piping component size:</strong> 2" ~ 24" (DN50-DN600)</li>
                  <li><strong>Pressure:</strong> ANSI Class 150~2500</li>
                  <li><strong>Temperature:</strong> -196℃~ 500℃</li>
                  <li><strong>Body Materials:</strong> Carbon Steel, Stainless Steel, Alloy Steel, Duplex Steel</li>
                  <li><strong>Trim (Wedge/Stem):</strong> Carbon Steel, Stainless Steel, Duplex Steel, Alloy Steel</li>
                </ul>
              </div>

              <div className="gv-spec-card-horizontal">
                <h3>Technical Specifications</h3>
                <ul>
                  <li><strong>Design:</strong> ASME 16.34/API 600</li>
                  <li><strong>Face to Face:</strong> ASME B16.10</li>
                  <li><strong>End Flange:</strong> ASME B16.5</li>
                  <li><strong>BW End:</strong> ASME B16.25</li>
                  <li><strong>Inspection and Testing:</strong> API 598</li>
                  <li><strong>Transmission:</strong> manual, electric, bevel gear</li>
                  <li><strong>Special:</strong> NACE MR-01-75, IBR</li>
                </ul>
              </div>

              <div className="gv-spec-card-horizontal">
                <h3>Design Features</h3>
                <ul>
                  <li>OS & Y, Rising stem, Non Rising Stem</li>
                  <li>Bolted Bonnet with spiral-wound gasket</li>
                  <li>Flexible Wedge, Solid Wedge</li>
                  <li>Backseat Weld-Deposited</li>
                  <li>Renewable seat rings (Welded in)</li>
                  <li>Extended Bonnet for Cryogenic Service</li>
                  <li>Flanged or butt welded ends</li>
                </ul>
            </div>
          </div>
            
            <div className="gv-specification-table-section">
              <h2 className="gv-table-title">Product Availability Matrix</h2>
              <img src={valvesMatrix} alt="Product Availability Matrix" />
              <p className="gv-legend">Legends : BB - Bolted Bonnet, BC - Bolted Cover, PS - Pressure Seal</p>
      </div>

            <div className="gv-material-options-section">
              <h2 className="gv-table-title">GTVMOC</h2>
              <div className="gv-material-content">
                <div className="gv-material-image">
                  <img src={picture1} alt="Gate Valve Parts" />
                </div>
                <div className="gv-material-table-container">
                  <table className="gv-material-table">
                    <thead>
                      <tr>
                        <th>PART NO</th>
                        <th>PART</th>
                        <th>MATERIAL OPTIONS</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>1</td>
                        <td>BODY</td>
                        <td>ASTM - A 216 WCB / WCC / LF2 / A 105 / A 351 CF8M / A 351 CF3M / A 352 LCB / LCC / DUPLEX SS / INCONEL / 254 SMO.</td>
                      </tr>
                      <tr>
                        <td>2</td>
                        <td>BONNET</td>
                        <td>ASTM - A 216 WCB / WCC / LF2 / A 105 / A 351 CF8M / A 351 CF3M / A 352 LCB / LCC / DUPLEX SS / INCONEL / 254 SMO.</td>
                      </tr>
                      <tr>
                        <td>3</td>
                        <td>SEAT RING</td>
                        <td>ASTM A105 + STELLITE 6 / A 351 CF8M / A 351 CF3M / A 217 CA 15 / DUPLEX SS / INCONEL / MONEL (HARD FACING AS PER REQUIREMENT).</td>
                      </tr>
                      <tr>
                        <td>4</td>
                        <td>WEDGE</td>
                        <td>ASTM - A 216 WCB + CRA OVERLAY / A 351 CF8M / A 351 CF3M / HARD FACING OVERLAY AS PER REQUIREMENT.</td>
                      </tr>
                      <tr>
                        <td>5</td>
                        <td>YOKE</td>
                        <td>ASTM - A 216 WCB / WCC / LF2 / A 105 / A 351 CF8M / A 351 CF3M / A 352 LCB / LCC / DUPLEX SS / INCONEL / 254 SMO.</td>
                      </tr>
                      <tr>
                        <td>6</td>
                        <td>STEM</td>
                        <td>A 479 SS 316 / A 479 SS 316L / A 182 F 6A / A 564 TYPE 630 / A 479 SS 410 / AISI 4140 + ENP / ASTM A 182 F51, F53, F44 / INCONEL (625, 825, 718).</td>
                      </tr>
                      <tr>
                        <td>7</td>
                        <td>BODY GASKET</td>
                        <td>GRAPHITE / REINFORCED SPIRAL-WOUND SS 316 GRAPHITE.</td>
                      </tr>
                      <tr>
                        <td>8</td>
                        <td>BACK SEAT BUSH</td>
                        <td>A 479 SS 316 / A 479 SS 316L / A 182 F 6A / A 564 TYPE 630 / A 479 SS 410 / AISI 4140 + ENP / ASTM A 182 F51, F53, F44 / INCONEL (625, 825, 718).</td>
                      </tr>
                      <tr>
                        <td>9</td>
                        <td>GLAND PACKING</td>
                        <td>DIE MOULDED GRAPHOIL / FLEXIBLE GRAPHITE WITH SACRIFICIAL CORROSION INHIBITOR AND INCONEL WIRE REINFORCEMENT / TOP & BOTTOM BRAIDED GRAPHITE.</td>
                      </tr>
                      <tr>
                        <td>10</td>
                        <td>GLAND BUSH</td>
                        <td>A 479 SS 316 / A 479 SS 316L / A 182 F 6A / A 564 TYPE 630 / A 479 SS 410 / AISI 4140 + ENP / ASTM A 182 F51, F53, F44 / INCONEL (625, 825, 718).</td>
                      </tr>
                      <tr>
                        <td>11</td>
                        <td>GLAND FLANGE</td>
                        <td>ASTM - A 216 WCB / WCC / LF2 / A 105 / A 351 CF8M / A 351 CF3M / A 352 LCB / LCC / DUPLEX SS / INCONEL / 254 SMO.</td>
                      </tr>
                      <tr>
                        <td>12</td>
                        <td>BODY STUDS / NUTS</td>
                        <td>ASTM - A 320 L7, L7M / A 193 B8M / A 193 B7 / A 193 GR. B7M, ASTM A194 GR. 7/7M, 2H, 2HM, 8, 8M.</td>
                      </tr>
                      <tr>
                        <td>13</td>
                        <td>GLAND STUD / NUTS</td>
                        <td>ASTM - A 320 L7, L7M / A 193 B8M / A 193 B7 / A 193 GR. B7M, ASTM A194 GR. 7/7M, 2H, 2HM, 8, 8M.</td>
                      </tr>
                      <tr>
                        <td>14</td>
                        <td>GEAR BOX MOUNTING BOLT</td>
                        <td>ASTM - A 320 L7, L7M / A 193 B8M / A 193 B7 / A 193 GR. B7M.</td>
                      </tr>
                      <tr>
                        <td>15</td>
                        <td>GEAR BOX</td>
                        <td>ASTM - A 313 SS 302 / ASTM B 637 (INCONEL 750) / INCONEL 718.</td>
                      </tr>
                    </tbody>
                  </table>
                  <p className="gv-material-note">Materials not listed above can be offered on request.</p>
            </div>
          </div>
      </div>

            {/* Dimensional Details Section */}
            <div className="gv-dimensional-details-section">
              <h2 className="gv-section-title">Gate Valve Dimensional Details</h2>
              
              {/* 150 Class Table */}
              <div className="gv-dimensional-table-wrapper">
                <h3 className="gv-dimensional-title">GATE VALVE DIMENSIONAL DETAILS FOR 150 CLASS</h3>
                <div className="gv-table-scroll-container">
                  <table className="gv-dimensional-table">
                    <thead>
                      <tr>
                        <th>Size</th>
                        <th>ØB</th>
                        <th colSpan="3">L</th>
                        <th>ØD</th>
                        <th>ØPCD</th>
                        <th>ØH</th>
                        <th>N</th>
                        <th>ØE</th>
                        <th>T</th>
                        <th>RF</th>
                        <th>ØHW</th>
                        <th>Weight Kg</th>
                      </tr>
                      <tr>
                        <th></th>
                        <th></th>
                        <th>RF</th>
                        <th>BWE</th>
                        <th>RTJ</th>
                        <th></th>
                        <th></th>
                        <th></th>
                        <th></th>
                        <th></th>
                        <th></th>
                        <th></th>
                        <th></th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr><td>2" (050)</td><td>51</td><td>178</td><td>216</td><td>191</td><td>152</td><td>152.4</td><td>19.1</td><td>4</td><td>91.9</td><td>14.2</td><td>1.5</td><td>200</td><td>19</td></tr>
                      <tr><td>3" (080)</td><td>77</td><td>203</td><td>282</td><td>216</td><td>190</td><td>152.4</td><td>19.1</td><td>4</td><td>127</td><td>17.5</td><td>1.5</td><td>250</td><td>35</td></tr>
                      <tr><td>4" (100)</td><td>102</td><td>229</td><td>305</td><td>242</td><td>229</td><td>190.5</td><td>19.1</td><td>8</td><td>157.2</td><td>22.4</td><td>1.5</td><td>250</td><td>49</td></tr>
                      <tr><td>6" (150)</td><td>153</td><td>267</td><td>403</td><td>280</td><td>279</td><td>241.3</td><td>22.2</td><td>8</td><td>215.9</td><td>23.9</td><td>1.5</td><td>300</td><td>81</td></tr>
                      <tr><td>8" (200)</td><td>204</td><td>292</td><td>419</td><td>305</td><td>343</td><td>298.4</td><td>22.2</td><td>8</td><td>269.7</td><td>26.9</td><td>1.5</td><td>350</td><td>127</td></tr>
                      <tr><td>10" (250)</td><td>254</td><td>330</td><td>457</td><td>343</td><td>406</td><td>362</td><td>25.4</td><td>12</td><td>323.9</td><td>28.4</td><td>1.5</td><td>400</td><td>204</td></tr>
                      <tr><td>12" (300)</td><td>305</td><td>356</td><td>502</td><td>369</td><td>483</td><td>431.8</td><td>25.4</td><td>12</td><td>381</td><td>30.2</td><td>1.5</td><td>450</td><td>291</td></tr>
                      <tr><td>14" (350)</td><td>337</td><td>381</td><td>572</td><td>394</td><td>533</td><td>476.2</td><td>28.6</td><td>12</td><td>412.8</td><td>33.3</td><td>1.5</td><td>450</td><td>400</td></tr>
                      <tr><td>16" (400)</td><td>388</td><td>406</td><td>610</td><td>419</td><td>597</td><td>539.8</td><td>28.6</td><td>16</td><td>469.9</td><td>35.1</td><td>1.5</td><td>450</td><td>486</td></tr>
                      <tr><td>18" (450)</td><td>439</td><td>432</td><td>660</td><td>445</td><td>635</td><td>577.8</td><td>31.8</td><td>16</td><td>533.4</td><td>38.1</td><td>1.5</td><td>450</td><td>610</td></tr>
                      <tr><td>20" (500)</td><td>489</td><td>457</td><td>711</td><td>470</td><td>693</td><td>635</td><td>31.8</td><td>20</td><td>584.2</td><td>41.1</td><td>1.5</td><td>450</td><td>788</td></tr>
                      <tr><td>24" (600)</td><td>591</td><td>508</td><td>813</td><td>521</td><td>813</td><td>749.3</td><td>35.1</td><td>20</td><td>692.2</td><td>46</td><td>1.5</td><td>450</td><td>1144</td></tr>
                    </tbody>
                  </table>
                </div>
                </div>

              {/* 300 Class Table */}
              <div className="gv-dimensional-table-wrapper">
                <h3 className="gv-dimensional-title">GATE VALVE DIMENSIONAL DETAILS FOR 300 CLASS</h3>
                <div className="gv-table-scroll-container">
                  <table className="gv-dimensional-table">
                    <thead>
                      <tr>
                        <th>Size</th>
                        <th>ØB</th>
                        <th colSpan="3">L</th>
                        <th>ØD</th>
                        <th>ØPCD</th>
                        <th>ØH</th>
                        <th>N</th>
                        <th>ØE</th>
                        <th>T</th>
                        <th>RF</th>
                        <th>ØHW</th>
                        <th>Weight Kg</th>
                      </tr>
                      <tr>
                        <th></th>
                        <th></th>
                        <th>RF</th>
                        <th>BWE</th>
                        <th>RTJ</th>
                        <th></th>
                        <th></th>
                        <th></th>
                        <th></th>
                        <th></th>
                        <th></th>
                        <th></th>
                        <th></th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr><td>2" (050)</td><td>51</td><td>216</td><td>216</td><td>232</td><td>165</td><td>127</td><td>19.1</td><td>8</td><td>91.9</td><td>20.6</td><td>1.5</td><td>250</td><td>26</td></tr>
                      <tr><td>3" (080)</td><td>77</td><td>282</td><td>282</td><td>298</td><td>210</td><td>168.1</td><td>22.2</td><td>8</td><td>127</td><td>26.9</td><td>1.5</td><td>300</td><td>52</td></tr>
                      <tr><td>4" (100)</td><td>102</td><td>305</td><td>305</td><td>321</td><td>254</td><td>200.2</td><td>22.2</td><td>8</td><td>157.2</td><td>30.2</td><td>1.5</td><td>300</td><td>75</td></tr>
                      <tr><td>6" (150)</td><td>153</td><td>403</td><td>403</td><td>419</td><td>318</td><td>269.7</td><td>22.2</td><td>12</td><td>215.9</td><td>35.1</td><td>1.5</td><td>400</td><td>147</td></tr>
                      <tr><td>8" (200)</td><td>204</td><td>419</td><td>419</td><td>435</td><td>381</td><td>330.2</td><td>25.4</td><td>12</td><td>269.7</td><td>39.6</td><td>1.5</td><td>500</td><td>235</td></tr>
                      <tr><td>10" (250)</td><td>254</td><td>457</td><td>457</td><td>473</td><td>444</td><td>387.4</td><td>28.6</td><td>16</td><td>323.9</td><td>46</td><td>1.5</td><td>500</td><td>350</td></tr>
                      <tr><td>12" (300)</td><td>305</td><td>502</td><td>502</td><td>518</td><td>521</td><td>450.8</td><td>31.8</td><td>16</td><td>381</td><td>30.2</td><td>1.5</td><td>500</td><td>472</td></tr>
                      <tr><td>14" (350)</td><td>337</td><td>762</td><td>762</td><td>778</td><td>584</td><td>514.4</td><td>31.8</td><td>20</td><td>412.8</td><td>52.3</td><td>1.5</td><td>500</td><td>683</td></tr>
                      <tr><td>16" (400)</td><td>388</td><td>838</td><td>838</td><td>854</td><td>648</td><td>571.5</td><td>35.1</td><td>20</td><td>469.9</td><td>55.6</td><td>1.5</td><td>600</td><td>950</td></tr>
                      <tr><td>18" (450)</td><td>432</td><td>914</td><td>914</td><td>930</td><td>711</td><td>628.6</td><td>35.1</td><td>24</td><td>533.4</td><td>58.7</td><td>1.5</td><td>600</td><td>1145</td></tr>
                      <tr><td>20" (500)</td><td>483</td><td>991</td><td>991</td><td>1010</td><td>775</td><td>685.8</td><td>35.1</td><td>24</td><td>584.2</td><td>62</td><td>1.5</td><td>600</td><td>1634</td></tr>
                      <tr><td>24" (600)</td><td>583</td><td>1143</td><td>1143</td><td>1165</td><td>914</td><td>812.8</td><td>41.1</td><td>24</td><td>692.2</td><td>68.3</td><td>1.5</td><td>600</td><td>2660</td></tr>
                    </tbody>
                  </table>
            </div>
          </div>

              {/* 600 Class Table */}
              <div className="gv-dimensional-table-wrapper">
                <h3 className="gv-dimensional-title">GATE VALVE DIMENSIONAL DETAILS FOR 600 CLASS</h3>
                <div className="gv-table-scroll-container">
                  <table className="gv-dimensional-table">
                    <thead>
                      <tr>
                        <th>Size</th>
                        <th>ØB</th>
                        <th colSpan="3">L</th>
                        <th>ØD</th>
                        <th>ØPCD</th>
                        <th>ØH</th>
                        <th>N</th>
                        <th>ØE</th>
                        <th>T</th>
                        <th>RF</th>
                        <th>ØHW</th>
                        <th>Weight Kg</th>
                      </tr>
                      <tr>
                        <th></th>
                        <th></th>
                        <th>RF</th>
                        <th>BWE</th>
                        <th>RTJ</th>
                        <th></th>
                        <th></th>
                        <th></th>
                        <th></th>
                        <th></th>
                        <th></th>
                        <th></th>
                        <th></th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr><td>2" (050)</td><td>51</td><td>292</td><td>292</td><td>295</td><td>165</td><td>127</td><td>19.1</td><td>8</td><td>91.9</td><td>25.4</td><td>6.4</td><td>300</td><td>39</td></tr>
                      <tr><td>3" (080)</td><td>77</td><td>356</td><td>356</td><td>359</td><td>210</td><td>168.1</td><td>22.2</td><td>8</td><td>127</td><td>31.8</td><td>6.4</td><td>350</td><td>68</td></tr>
                      <tr><td>4" (100)</td><td>102</td><td>432</td><td>432</td><td>435</td><td>273</td><td>215.9</td><td>25.4</td><td>8</td><td>157.2</td><td>38.1</td><td>6.4</td><td>450</td><td>120</td></tr>
                      <tr><td>6" (150)</td><td>153</td><td>559</td><td>559</td><td>562</td><td>356</td><td>292.1</td><td>28.6</td><td>12</td><td>215.9</td><td>47.8</td><td>6.4</td><td>550</td><td>273</td></tr>
                      <tr><td>8" (200)</td><td>200</td><td>660</td><td>660</td><td>663</td><td>419</td><td>349.2</td><td>31.8</td><td>12</td><td>269.7</td><td>55.6</td><td>6.4</td><td>600</td><td>402</td></tr>
                      <tr><td>10" (250)</td><td>254</td><td>787</td><td>787</td><td>790</td><td>508</td><td>431.8</td><td>35.1</td><td>16</td><td>323.9</td><td>63.5</td><td>6.4</td><td>600</td><td>610</td></tr>
                      <tr><td>12" (300)</td><td>299</td><td>838</td><td>838</td><td>841</td><td>559</td><td>489</td><td>35.1</td><td>20</td><td>381</td><td>66.5</td><td>6.4</td><td>600</td><td>902</td></tr>
                      <tr><td>14" (350)</td><td>327</td><td>889</td><td>889</td><td>892</td><td>603</td><td>527</td><td>38.1</td><td>20</td><td>412.8</td><td>69.8</td><td>6.4</td><td>600</td><td>1245</td></tr>
                      <tr><td>16" (400)</td><td>375</td><td>991</td><td>991</td><td>994</td><td>686</td><td>603.2</td><td>41.1</td><td>20</td><td>469.9</td><td>76.2</td><td>6.4</td><td>750</td><td>1530</td></tr>
                      <tr><td>18" (450)</td><td>420</td><td>1092</td><td>1092</td><td>1095</td><td>743</td><td>654</td><td>44.5</td><td>20</td><td>533.4</td><td>82.6</td><td>6.4</td><td>750</td><td>2030</td></tr>
                      <tr><td>20" (500)</td><td>464</td><td>1194</td><td>1194</td><td>1200</td><td>813</td><td>723.9</td><td>44.5</td><td>24</td><td>584.2</td><td>88.9</td><td>6.4</td><td>750</td><td>2735</td></tr>
                      <tr><td>24" (600)</td><td>559</td><td>1397</td><td>1397</td><td>1407</td><td>940</td><td>838.2</td><td>50.8</td><td>24</td><td>692.2</td><td>102</td><td>6.4</td><td>750</td><td>3620</td></tr>
                    </tbody>
                  </table>
                </div>
      </div>

            {/* PSGTV MOC Section */}
            <div className="gv-psgtv-moc-section">
              <h2 className="gv-table-title">PSGTV MOC</h2>
              <div className="gv-psgtv-content">
                <div className="gv-psgtv-image">
                  <img src={picture2} alt="Pressure Seal Gate Valve Parts" />
                </div>
                <div className="gv-psgtv-table-container">
                  <table className="gv-material-table">
                    <thead>
                      <tr>
                        <th>PART NO</th>
                        <th>PART</th>
                        <th>MATERIAL OPTIONS</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>1</td>
                        <td>BODY</td>
                        <td>ASTM - A 216 WCB / WCC / LF2 / A 105 / A 351 CF8M / A 351 CF3M / A 352 LCB / LCC / DUPLEX SS / INCONEL / 254 SMO.</td>
                      </tr>
                      <tr>
                        <td>2</td>
                        <td>BONNET</td>
                        <td>ASTM - A 216 WCB / WCC / LF2 / A 105 / A 351 CF8M / A 351 CF3M / A 352 LCB / LCC / DUPLEX SS / INCONEL / 254 SMO.</td>
                      </tr>
                      <tr>
                        <td>3</td>
                        <td>SEAT RING</td>
                        <td>ASTM A105 + STELLITE 6 / A 351 CF8M / A 351 CF3M / A 217 CA 15 / DUPLEX SS / INCONEL / MONEL (HARD FACING AS PER REQUIREMENT).</td>
                      </tr>
                      <tr>
                        <td>4</td>
                        <td>WEDGE</td>
                        <td>ASTM - A 216 WCB + CRA OVERLAY / A351 CF8M / A 351 CF3M / HARD FACING OVERLAY AS PER REQUIREMENT.</td>
                      </tr>
                      <tr>
                        <td>5</td>
                        <td>YOKE</td>
                        <td>ASTM - A 216 WCB / WCC / LF2 / A 105 / A 351 CF8M / A 351 CF3M / A 352 LCB / LCC / DUPLEX SS / INCONEL / 254 SMO.</td>
                      </tr>
                      <tr>
                        <td>6</td>
                        <td>STEM</td>
                        <td>A 479 SS 316 / A 479 SS 316L / A 182 F 6A / A 564 TYPE 630 / A 479 SS 410 / AISI 4140 + ENP / ASTM A 182 F51, F53, F44 / INCONEL (625, 825, 718).</td>
                      </tr>
                      <tr>
                        <td>7</td>
                        <td>PRESSURE SEAL GASKET</td>
                        <td>GRAPHITE / DIE MOULDED GRAPHITE.</td>
                      </tr>
                      <tr>
                        <td>8</td>
                        <td>SPACER RING</td>
                        <td>A 479 SS 316 / A 479 SS 316L / A 182 F 6A / A 564 TYPE 630 / A 479 SS 410, ASTM A 182 F51, F53, F44 / INCONEL (625, 825, 718).</td>
                      </tr>
                      <tr>
                        <td>9</td>
                        <td>SEGMENTAL RING</td>
                        <td>A 479 SS 316 / A 479 SS 316L / A 182 F 6A / A 564 TYPE 630 / A 479 SS 410, ASTM A 182 F51, F53, F44 / INCONEL (625, 825, 718).</td>
                      </tr>
                      <tr>
                        <td>10</td>
                        <td>FACE RING</td>
                        <td>ASTM - A 216 WCB / WCC / LF2 / A 105 / A 351 CF8M / A 351 CF3M / A 352 LCB / LCC / DUPLEX SS / INCONEL / 254 SMO.</td>
                      </tr>
                      <tr>
                        <td>11</td>
                        <td>LOCKING RING</td>
                        <td>ASTM - A 216 WCB / WCC / LF2 / A 105 / A 351 CF8M / A 351 CF3M / A 352 LCB / LCC / DUPLEX SS / INCONEL / 254 SMO.</td>
                      </tr>
                      <tr>
                        <td>12</td>
                        <td>BACK SEAT BUSH</td>
                        <td>A 479 SS 316 / A 479 SS 316L / A 182 F 6A / A 564 TYPE 630 / A 479 SS 410 / AISI 4140 + ENP / ASTM A 182 F51, F53, F44 / INCONEL (625, 825, 718).</td>
                      </tr>
                      <tr>
                        <td>13</td>
                        <td>GLAND PACKING</td>
                        <td>DIE MOULDED GRAPHOIL / FLEXIBLE GRAPHITE WITH SACRIFICIAL CORROSION INHIBITOR AND INCONEL WIRE REINFORCEMENT / TOP & BOTTOM BRAIDED GRAPHITE.</td>
                      </tr>
                      <tr>
                        <td>14</td>
                        <td>GLAND BUSH</td>
                        <td>A 479 SS 316 / A 479 SS 316L / A 182 F 6A / A 564 TYPE 630 / A 479 SS 410 / AISI 4140 + ENP / ASTM A 182 F51, F53, F44 / INCONEL (625, 825, 718).</td>
                      </tr>
                      <tr>
                        <td>15</td>
                        <td>GLAND FLANGE</td>
                        <td>ASTM - A 216 WCB / WCC / LF2 / A 105 / A 351 CF8M / A 351 CF3M / A 352 LCB / LCC / DUPLEX SS / INCONEL / 254 SMO.</td>
                      </tr>
                      <tr>
                        <td>16</td>
                        <td>GEAR BOX</td>
                        <td>COMMERCIAL.</td>
                      </tr>
                      <tr>
                        <td>17</td>
                        <td>YOKE STUDS / NUTS</td>
                        <td>ASTM - A 320 L7, L7M / A 193 B8M / A 193 B7 / A 193 GR. B7M, ASTM A194 GR. 7/7M, 2H, 2HM, 8, 8M.</td>
                      </tr>
                      <tr>
                        <td>18</td>
                        <td>GLAND STUD / NUTS</td>
                        <td>ASTM - A 320 L7, L7M / A 193 B8M / A 193 B7 / A 193 GR. B7M, ASTM A194 GR. 7/7M, 2H, 2HM, 8, 8M.</td>
                      </tr>
                      <tr>
                        <td>19</td>
                        <td>HANDWHEEL NUT</td>
                        <td>STEEL.</td>
                      </tr>
                      <tr>
                        <td>20</td>
                        <td>HANDWHEEL HOUSING BOLTS</td>
                        <td>HIGH TENSILE.</td>
                      </tr>
                      <tr>
                        <td>21</td>
                        <td>HANDWHEEL HOUSING</td>
                        <td>HIGH TENSILE.</td>
                      </tr>
                      <tr>
                        <td>22</td>
                        <td>YOKE SLEEVE</td>
                        <td>ASTM A439 Gr.D2 / PHOSPHOROUS BRONZE.</td>
                      </tr>
                    </tbody>
                  </table>
                  <p className="gv-material-note">Note: Materials not listed above can be offered on request.</p>
                </div>
            </div>
          </div>

            {/* Pressure Seal Gate Valve Dimensional Details Section */}
            <div className="gv-dimensional-details-section">
              <h2 className="gv-section-title">Pressure Seal Gate Valve Dimensional Details</h2>
              
              {/* 900 Class Table */}
              <div className="gv-dimensional-table-wrapper">
                <h3 className="gv-dimensional-title">PRESSURE SEAL GATE VALVE DIMENSIONAL DETAILS FOR 900 CLASS</h3>
                <div className="gv-table-scroll-container">
                  <table className="gv-dimensional-table">
                    <thead>
                      <tr>
                        <th>Size</th>
                        <th>ØB</th>
                        <th colSpan="3">L</th>
                        <th>ØD</th>
                        <th>ØPCD</th>
                        <th>ØH</th>
                        <th>N</th>
                        <th>ØE</th>
                        <th>T</th>
                        <th>RF</th>
                        <th>ØHW</th>
                        <th>Weight Kg</th>
                      </tr>
                      <tr>
                        <th></th>
                        <th></th>
                        <th>RF</th>
                        <th>BWE</th>
                        <th>RTJ</th>
                        <th></th>
                        <th></th>
                        <th></th>
                        <th></th>
                        <th></th>
                        <th></th>
                        <th></th>
                        <th></th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr><td>2" (050)</td><td>48</td><td>368</td><td>368</td><td>371</td><td>216</td><td>165.1</td><td>25.4</td><td>8</td><td>91.9</td><td>38.1</td><td>6.4</td><td>350</td><td>63</td></tr>
                      <tr><td>3" (080)</td><td>73</td><td>381</td><td>381</td><td>384</td><td>241</td><td>190.5</td><td>25.4</td><td>8</td><td>127</td><td>38.1</td><td>6.4</td><td>350</td><td>107</td></tr>
                      <tr><td>4" (100)</td><td>99</td><td>457</td><td>457</td><td>460</td><td>292</td><td>235</td><td>31.8</td><td>8</td><td>157.2</td><td>44.5</td><td>6.4</td><td>450</td><td>158</td></tr>
                      <tr><td>6" (150)</td><td>147</td><td>610</td><td>610</td><td>613</td><td>381</td><td>317.5</td><td>31.8</td><td>12</td><td>215.9</td><td>55.6</td><td>6.4</td><td>600</td><td>294</td></tr>
                      <tr><td>8" (200)</td><td>191</td><td>737</td><td>737</td><td>740</td><td>470</td><td>393.7</td><td>38.1</td><td>12</td><td>269.7</td><td>63.5</td><td>6.4</td><td>600</td><td>693</td></tr>
                      <tr><td>10" (250)</td><td>238</td><td>838</td><td>838</td><td>841</td><td>546</td><td>469.9</td><td>38.1</td><td>16</td><td>323.9</td><td>69.8</td><td>6.4</td><td>750</td><td>1008</td></tr>
                      <tr><td>12" (300)</td><td>283</td><td>965</td><td>965</td><td>968</td><td>610</td><td>533.4</td><td>38.1</td><td>20</td><td>381</td><td>79.2</td><td>6.4</td><td>750</td><td>1418</td></tr>
                    </tbody>
                  </table>
                </div>
      </div>

              {/* 1500 Class Table */}
              <div className="gv-dimensional-table-wrapper">
                <h3 className="gv-dimensional-title">PRESSURE SEAL GATE VALVE DIMENSIONAL DETAILS FOR 1500 CLASS</h3>
                <div className="gv-table-scroll-container">
                  <table className="gv-dimensional-table">
                    <thead>
                      <tr>
                        <th>Size</th>
                        <th>ØB</th>
                        <th colSpan="3">L</th>
                        <th>ØD</th>
                        <th>ØPCD</th>
                        <th>ØH</th>
                        <th>N</th>
                        <th>ØE</th>
                        <th>T</th>
                        <th>RF</th>
                        <th>ØHW</th>
                        <th>Weight Kg</th>
                      </tr>
                      <tr>
                        <th></th>
                        <th></th>
                        <th>RF</th>
                        <th>BWE</th>
                        <th>RTJ</th>
                        <th></th>
                        <th></th>
                        <th></th>
                        <th></th>
                        <th></th>
                        <th></th>
                        <th></th>
                        <th></th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr><td>2" (050)</td><td>48</td><td>368</td><td>368</td><td>371</td><td>216</td><td>165.1</td><td>25.4</td><td>8</td><td>91.9</td><td>38.1</td><td>6.4</td><td>350</td><td>63</td></tr>
                      <tr><td>3" (080)</td><td>70</td><td>470</td><td>470</td><td>473</td><td>267</td><td>203.2</td><td>31.8</td><td>8</td><td>127</td><td>47.8</td><td>6.4</td><td>750</td><td>126</td></tr>
                      <tr><td>4" (100)</td><td>92</td><td>546</td><td>546</td><td>549</td><td>311</td><td>241.3</td><td>35.1</td><td>8</td><td>157.2</td><td>53.8</td><td>6.4</td><td>550</td><td>218</td></tr>
                      <tr><td>6" (150)</td><td>137</td><td>705</td><td>705</td><td>711</td><td>394</td><td>317.5</td><td>38.1</td><td>12</td><td>215.9</td><td>82.6</td><td>6.4</td><td>600</td><td>504</td></tr>
                      <tr><td>8" (200)</td><td>178</td><td>832</td><td>832</td><td>842</td><td>483</td><td>393.7</td><td>41.1</td><td>12</td><td>269.7</td><td>91.9</td><td>6.4</td><td>750</td><td>956</td></tr>
                      <tr><td>10" (250)</td><td>223</td><td>991</td><td>991</td><td>1001</td><td>584</td><td>482.6</td><td>50.8</td><td>12</td><td>323.9</td><td>108</td><td>6.4</td><td>750</td><td>1481</td></tr>
                      <tr><td>12" (300)</td><td>264</td><td>1130</td><td>1130</td><td>1146</td><td>673</td><td>571.5</td><td>53.9</td><td>16</td><td>381</td><td>124</td><td>6.4</td><td>750</td><td>2184</td></tr>
                    </tbody>
                  </table>
                </div>
                </div>

              {/* 2500 Class Table */}
              <div className="gv-dimensional-table-wrapper">
                <h3 className="gv-dimensional-title">PRESSURE SEAL GATE VALVE DIMENSIONAL DETAILS FOR 2500 CLASS</h3>
                <div className="gv-table-scroll-container">
                  <table className="gv-dimensional-table">
                    <thead>
                      <tr>
                        <th>Size</th>
                        <th>ØB</th>
                        <th colSpan="3">L</th>
                        <th>ØD</th>
                        <th>ØPCD</th>
                        <th>ØH</th>
                        <th>N</th>
                        <th>ØE</th>
                        <th>T</th>
                        <th>RF</th>
                        <th>ØHW</th>
                        <th>Weight Kg</th>
                      </tr>
                      <tr>
                        <th></th>
                        <th></th>
                        <th>RF</th>
                        <th>BWE</th>
                        <th>RTJ</th>
                        <th></th>
                        <th></th>
                        <th></th>
                        <th></th>
                        <th></th>
                        <th></th>
                        <th></th>
                        <th></th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr><td>2" (050)</td><td>39</td><td>451</td><td>451</td><td>454</td><td>235</td><td>171.4</td><td>28.6</td><td>8</td><td>91.9</td><td>50.8</td><td>6.4</td><td>450</td><td>89</td></tr>
                      <tr><td>3" (080)</td><td>58</td><td>578</td><td>578</td><td>584</td><td>305</td><td>228.6</td><td>35.1</td><td>8</td><td>127</td><td>66.5</td><td>6.4</td><td>600</td><td>173</td></tr>
                      <tr><td>4" (100)</td><td>73</td><td>673</td><td>673</td><td>683</td><td>356</td><td>273</td><td>41.3</td><td>8</td><td>157.2</td><td>76.2</td><td>6.4</td><td>600</td><td>346</td></tr>
                      <tr><td>6" (150)</td><td>111</td><td>914</td><td>914</td><td>927</td><td>483</td><td>368.3</td><td>53.9</td><td>8</td><td>215.9</td><td>108.0</td><td>6.4</td><td>750</td><td>851</td></tr>
                      <tr><td>8" (200)</td><td>147</td><td>1022</td><td>1022</td><td>1038</td><td>552</td><td>438.2</td><td>53.9</td><td>12</td><td>269.7</td><td>127.0</td><td>6.4</td><td>750</td><td>1386</td></tr>
                      <tr><td>10" (250)</td><td>185</td><td>1270</td><td>1270</td><td>1292</td><td>673</td><td>539.8</td><td>66.7</td><td>12</td><td>323.9</td><td>165.1</td><td>6.4</td><td>750</td><td>2493</td></tr>
                      <tr><td>12" (300)</td><td>219</td><td>1422</td><td>1422</td><td>1444</td><td>762</td><td>619.3</td><td>73</td><td>12</td><td>381</td><td>184.2</td><td>6.4</td><td>750</td><td>3091</td></tr>
                    </tbody>
                  </table>
            </div>
          </div>
      </div>

            {/* Actuator Performance Table */}
            <div className="gv-actuator-performance-section">
              <h2 className="gv-section-title">Actuator Performance Data</h2>
              <div className="gv-actuator-table-wrapper">
                <div className="gv-table-scroll-container">
                  <table className="gv-actuator-table">
                    <thead>
                      <tr>
                        <th rowSpan="2">CLASS SIZE</th>
                        <th colSpan="60">SIZE</th>
                      </tr>
                      <tr>
                        <th colSpan="5">2"</th>
                        <th colSpan="5">3"</th>
                        <th colSpan="5">4"</th>
                        <th colSpan="5">6"</th>
                        <th colSpan="5">8"</th>
                        <th colSpan="5">10"</th>
                        <th colSpan="5">12"</th>
                        <th colSpan="5">14"</th>
                        <th colSpan="5">16"</th>
                        <th colSpan="5">18"</th>
                        <th colSpan="5">20"</th>
                        <th colSpan="5">24"</th>
                      </tr>
                      <tr>
                        <th></th>
                        <th>0.2</th><th>0.5</th><th>1</th><th>1.5</th><th>2</th>
                        <th>0.2</th><th>0.5</th><th>1</th><th>1.5</th><th>2</th>
                        <th>0.2</th><th>0.5</th><th>1</th><th>1.5</th><th>2</th>
                        <th>0.2</th><th>0.5</th><th>1</th><th>1.5</th><th>2</th>
                        <th></th><th></th><th></th><th></th><th></th>
                        <th></th><th></th><th></th><th></th><th></th>
                        <th></th><th></th><th></th><th></th><th></th>
                        <th></th><th></th><th></th><th></th><th></th>
                        <th></th><th></th><th></th><th></th><th></th>
                        <th></th><th></th><th></th><th></th><th></th>
                        <th></th><th></th><th></th><th></th><th></th>
                        <th></th><th></th><th></th><th></th><th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {/* CLASS SIZE 150 */}
                      <tr className="gv-class-row">
                        <td rowSpan="4" className="gv-class-cell">150</td>
                        <td className="gv-data-label">PRESSURE RATING (MPa)</td>
                        <td>0.2</td><td>0.5</td><td>1</td><td>1.5</td><td>2</td>
                        <td>0.2</td><td>0.5</td><td>1</td><td>1.5</td><td>2</td>
                        <td>0.2</td><td>0.5</td><td>1</td><td>1.5</td><td>2</td>
                        <td>0.2</td><td>0.5</td><td>1</td><td>1.5</td><td>2</td>
                        <td></td><td></td><td></td><td></td><td></td>
                        <td></td><td></td><td></td><td></td><td></td>
                        <td></td><td></td><td></td><td></td><td></td>
                        <td></td><td></td><td></td><td></td><td></td>
                        <td></td><td></td><td></td><td></td><td></td>
                        <td></td><td></td><td></td><td></td><td></td>
                        <td></td><td></td><td></td><td></td><td></td>
                        <td></td><td></td><td></td><td></td><td></td>
                      </tr>
                      <tr>
                        <td className="gv-data-label">NO. OF TURNS</td>
                        <td colSpan="5">14</td>
                        <td colSpan="5">20</td>
                        <td colSpan="5">22</td>
                        <td colSpan="5">33</td>
                        <td colSpan="5">43</td>
                        <td colSpan="5">42</td>
                        <td colSpan="5">51</td>
                        <td colSpan="5">55</td>
                        <td colSpan="5">64</td>
                        <td colSpan="5">73</td>
                        <td colSpan="5">81</td>
                        <td colSpan="5">74</td>
                      </tr>
                      <tr>
                        <td className="gv-data-label">THRUST (KN)</td>
                        <td>6.9</td><td>7.2</td><td>7.8</td><td>8.4</td><td>9</td>
                        <td>8.9</td><td>9.5</td><td>10.3</td><td>11.1</td><td>12</td>
                        <td>11.15</td><td>11.88</td><td>13</td><td>14.18</td><td>15.33</td>
                        <td>16.9</td><td>17.8</td><td>19.8</td><td>21.5</td><td>23.5</td>
                        <td></td><td></td><td></td><td></td><td></td>
                        <td></td><td></td><td></td><td></td><td></td>
                        <td></td><td></td><td></td><td></td><td></td>
                        <td></td><td></td><td></td><td></td><td></td>
                        <td></td><td></td><td></td><td></td><td></td>
                        <td></td><td></td><td></td><td></td><td></td>
                        <td></td><td></td><td></td><td></td><td></td>
                        <td></td><td></td><td></td><td></td><td></td>
                      </tr>
                      <tr>
                        <td className="gv-data-label">TORQUE (Nm)</td>
                        <td>13.2</td><td>13.9</td><td>15</td><td>16.1</td><td>17.3</td>
                        <td>19.4</td><td>20.4</td><td>22.3</td><td>24.1</td><td>26</td>
                        <td>27.9</td><td>29.7</td><td>32.5</td><td>35.4</td><td>38.3</td>
                        <td>46.2</td><td>49</td><td>53</td><td>58.8</td><td>63.4</td>
                        <td></td><td></td><td></td><td></td><td></td>
                        <td></td><td></td><td></td><td></td><td></td>
                        <td></td><td></td><td></td><td></td><td></td>
                        <td></td><td></td><td></td><td></td><td></td>
                        <td></td><td></td><td></td><td></td><td></td>
                        <td></td><td></td><td></td><td></td><td></td>
                        <td></td><td></td><td></td><td></td><td></td>
                        <td></td><td></td><td></td><td></td><td></td>
                      </tr>
                      {/* CLASS SIZE 300 */}
                      <tr className="gv-class-row">
                        <td rowSpan="4" className="gv-class-cell">300</td>
                        <td className="gv-data-label">PRESSURE RATING (MPa)</td>
                        <td>1</td><td>2</td><td>3</td><td>4</td><td>5.1</td>
                        <td>1</td><td>2</td><td>3</td><td>4</td><td>5.1</td>
                        <td>1</td><td>2</td><td>3</td><td>4</td><td>5.1</td>
                        <td></td><td></td><td></td><td></td><td></td>
                        <td></td><td></td><td></td><td></td><td></td>
                        <td></td><td></td><td></td><td></td><td></td>
                        <td></td><td></td><td></td><td></td><td></td>
                        <td></td><td></td><td></td><td></td><td></td>
                        <td></td><td></td><td></td><td></td><td></td>
                        <td></td><td></td><td></td><td></td><td></td>
                        <td></td><td></td><td></td><td></td><td></td>
                        <td></td><td></td><td></td><td></td><td></td>
                        <td></td><td></td><td></td><td></td><td></td>
                      </tr>
                      <tr>
                        <td className="gv-data-label">NO. OF TURNS</td>
                        <td colSpan="5">14</td>
                        <td colSpan="5">20</td>
                        <td colSpan="5">22</td>
                        <td colSpan="5">33</td>
                        <td colSpan="5">35</td>
                        <td colSpan="5">42</td>
                        <td colSpan="5">51</td>
                        <td colSpan="5">55</td>
                        <td colSpan="5">64</td>
                        <td colSpan="5">71</td>
                        <td colSpan="5">61</td>
                        <td colSpan="5">65</td>
                      </tr>
                      <tr>
                        <td className="gv-data-label">THRUST (KN)</td>
                        <td>7.8</td><td>9</td><td>10.1</td><td>11.3</td><td>12.6</td>
                        <td>10.2</td><td>12</td><td>13.5</td><td>15.5</td><td>17.1</td>
                        <td>13</td><td>15.5</td><td>17.7</td><td>20.1</td><td>24.1</td>
                        <td></td><td></td><td></td><td></td><td></td>
                        <td></td><td></td><td></td><td></td><td></td>
                        <td></td><td></td><td></td><td></td><td></td>
                        <td></td><td></td><td></td><td></td><td></td>
                        <td></td><td></td><td></td><td></td><td></td>
                        <td></td><td></td><td></td><td></td><td></td>
                        <td></td><td></td><td></td><td></td><td></td>
                        <td></td><td></td><td></td><td></td><td></td>
                        <td></td><td></td><td></td><td></td><td></td>
                      </tr>
                      <tr>
                        <td className="gv-data-label">TORQUE (Nm)</td>
                        <td>15</td><td>17.3</td><td>19.5</td><td>21.8</td><td>24.3</td>
                        <td>22.3</td><td>26</td><td>29.5</td><td>33.2</td><td>37.3</td>
                        <td>32.5</td><td>38.3</td><td>44</td><td>50</td><td>60.1</td>
                        <td></td><td></td><td></td><td></td><td></td>
                        <td></td><td></td><td></td><td></td><td></td>
                        <td></td><td></td><td></td><td></td><td></td>
                        <td></td><td></td><td></td><td></td><td></td>
                        <td></td><td></td><td></td><td></td><td></td>
                        <td></td><td></td><td></td><td></td><td></td>
                        <td></td><td></td><td></td><td></td><td></td>
                        <td></td><td></td><td></td><td></td><td></td>
                        <td></td><td></td><td></td><td></td><td></td>
                      </tr>
                      {/* CLASS SIZE 600 */}
                      <tr className="gv-class-row">
                        <td rowSpan="4" className="gv-class-cell">600</td>
                        <td className="gv-data-label">PRESSURE RATING (MPa)</td>
                        <td>3</td><td>5</td><td>6.5</td><td>8</td><td>10.2</td>
                        <td>3</td><td>5</td><td>6.5</td><td>8</td><td>10.2</td>
                        <td>3</td><td>5</td><td>6.5</td><td>8</td><td>10.2</td>
                        <td></td><td></td><td></td><td></td><td></td>
                        <td></td><td></td><td></td><td></td><td></td>
                        <td></td><td></td><td></td><td></td><td></td>
                        <td></td><td></td><td></td><td></td><td></td>
                        <td></td><td></td><td></td><td></td><td></td>
                        <td></td><td></td><td></td><td></td><td></td>
                        <td></td><td></td><td></td><td></td><td></td>
                        <td></td><td></td><td></td><td></td><td></td>
                        <td></td><td></td><td></td><td></td><td></td>
                        <td></td><td></td><td></td><td></td><td></td>
                      </tr>
                      <tr>
                        <td className="gv-data-label">NO. OF TURNS</td>
                        <td colSpan="5">14</td>
                        <td colSpan="5">17</td>
                        <td colSpan="5">23</td>
                        <td colSpan="5">28</td>
                        <td colSpan="5">34</td>
                        <td colSpan="5">42</td>
                        <td colSpan="5">50</td>
                        <td colSpan="5">41</td>
                        <td colSpan="5">47</td>
                        <td colSpan="5">53</td>
                        <td colSpan="5">59</td>
                        <td colSpan="5">46</td>
                      </tr>
                      <tr>
                        <td className="gv-data-label">THRUST (KN)</td>
                        <td>12.7</td><td>14.5</td><td>16.2</td><td>18.04</td><td>20.6</td>
                        <td>16.8</td><td>20.4</td><td>23.2</td><td>26.8</td><td>32.1</td>
                        <td>24.2</td><td>30.7</td><td>36.5</td><td>42.3</td><td>50.9</td>
                        <td></td><td></td><td></td><td></td><td></td>
                        <td></td><td></td><td></td><td></td><td></td>
                        <td></td><td></td><td></td><td></td><td></td>
                        <td></td><td></td><td></td><td></td><td></td>
                        <td></td><td></td><td></td><td></td><td></td>
                        <td></td><td></td><td></td><td></td><td></td>
                        <td></td><td></td><td></td><td></td><td></td>
                        <td></td><td></td><td></td><td></td><td></td>
                        <td></td><td></td><td></td><td></td><td></td>
                      </tr>
                      <tr>
                        <td className="gv-data-label">TORQUE (Nm)</td>
                        <td>23.4</td><td>27.9</td><td>31.3</td><td>34.6</td><td>39.6</td>
                        <td>42.3</td><td>51.2</td><td>58.2</td><td>67.2</td><td>81.3</td>
                        <td>66.2</td><td>83.9</td><td>99.8</td><td>115.8</td><td>139.2</td>
                        <td></td><td></td><td></td><td></td><td></td>
                        <td></td><td></td><td></td><td></td><td></td>
                        <td></td><td></td><td></td><td></td><td></td>
                        <td></td><td></td><td></td><td></td><td></td>
                        <td></td><td></td><td></td><td></td><td></td>
                        <td></td><td></td><td></td><td></td><td></td>
                        <td></td><td></td><td></td><td></td><td></td>
                        <td></td><td></td><td></td><td></td><td></td>
                        <td></td><td></td><td></td><td></td><td></td>
                      </tr>
                      {/* CLASS SIZE 900 */}
                      <tr className="gv-class-row">
                        <td rowSpan="4" className="gv-class-cell">900</td>
                        <td className="gv-data-label">PRESSURE RATING (MPa)</td>
                        <td>5</td><td>8</td><td>10</td><td>13</td><td>15.3</td>
                        <td>5</td><td>8</td><td>10</td><td>13</td><td>15.3</td>
                        <td>5</td><td>8</td><td>10</td><td>13</td><td>15.3</td>
                        <td></td><td></td><td></td><td></td><td></td>
                        <td></td><td></td><td></td><td></td><td></td>
                        <td></td><td></td><td></td><td></td><td></td>
                        <td></td><td></td><td></td><td></td><td></td>
                        <td></td><td></td><td></td><td></td><td></td>
                        <td></td><td></td><td></td><td></td><td></td>
                        <td></td><td></td><td></td><td></td><td></td>
                        <td></td><td></td><td></td><td></td><td></td>
                        <td></td><td></td><td></td><td></td><td></td>
                        <td></td><td></td><td></td><td></td><td></td>
                      </tr>
                      <tr>
                        <td className="gv-data-label">NO. OF TURNS</td>
                        <td colSpan="5">13</td>
                        <td colSpan="5">18</td>
                        <td colSpan="5">23</td>
                        <td colSpan="5">28</td>
                        <td colSpan="5">34</td>
                        <td colSpan="5">31</td>
                        <td colSpan="5">36</td>
                        <td colSpan="5"></td>
                        <td colSpan="5"></td>
                        <td colSpan="5"></td>
                        <td colSpan="5"></td>
                        <td colSpan="5"></td>
                      </tr>
                      <tr>
                        <td className="gv-data-label">THRUST (KN)</td>
                        <td>17.9</td><td>22.3</td><td>25.3</td><td>29.7</td><td>33.1</td>
                        <td>26.2</td><td>32.7</td><td>37.1</td><td>43.5</td><td>49.2</td>
                        <td>32.2</td><td>42.6</td><td>50.3</td><td>61.8</td><td>70.6</td>
                        <td></td><td></td><td></td><td></td><td></td>
                        <td></td><td></td><td></td><td></td><td></td>
                        <td></td><td></td><td></td><td></td><td></td>
                        <td></td><td></td><td></td><td></td><td></td>
                        <td></td><td></td><td></td><td></td><td></td>
                        <td></td><td></td><td></td><td></td><td></td>
                        <td></td><td></td><td></td><td></td><td></td>
                        <td></td><td></td><td></td><td></td><td></td>
                        <td></td><td></td><td></td><td></td><td></td>
                      </tr>
                      <tr>
                        <td className="gv-data-label">TORQUE (Nm)</td>
                        <td>30.8</td><td>38.4</td><td>43.4</td><td>51</td><td>56.8</td>
                        <td>37.4</td><td>64</td><td>72.4</td><td>85</td><td>96.5</td>
                        <td>95.7</td><td>125.7</td><td>179.5</td><td>183.7</td><td>209.9</td>
                        <td></td><td></td><td></td><td></td><td></td>
                        <td></td><td></td><td></td><td></td><td></td>
                        <td></td><td></td><td></td><td></td><td></td>
                        <td></td><td></td><td></td><td></td><td></td>
                        <td></td><td></td><td></td><td></td><td></td>
                        <td></td><td></td><td></td><td></td><td></td>
                        <td></td><td></td><td></td><td></td><td></td>
                        <td></td><td></td><td></td><td></td><td></td>
                        <td></td><td></td><td></td><td></td><td></td>
                      </tr>
                      {/* CLASS SIZE 1500 */}
                      <tr className="gv-class-row">
                        <td rowSpan="4" className="gv-class-cell">1500</td>
                        <td className="gv-data-label">PRESSURE RATING (MPa)</td>
                        <td>5</td><td>10</td><td>15</td><td>20</td><td>25.5</td>
                        <td>5</td><td>10</td><td>15</td><td>20</td><td>25.5</td>
                        <td>5</td><td>10</td><td>15</td><td>20</td><td>25.5</td>
                        <td></td><td></td><td></td><td></td><td></td>
                        <td></td><td></td><td></td><td></td><td></td>
                        <td></td><td></td><td></td><td></td><td></td>
                        <td></td><td></td><td></td><td></td><td></td>
                        <td></td><td></td><td></td><td></td><td></td>
                        <td></td><td></td><td></td><td></td><td></td>
                        <td></td><td></td><td></td><td></td><td></td>
                        <td></td><td></td><td></td><td></td><td></td>
                        <td></td><td></td><td></td><td></td><td></td>
                        <td></td><td></td><td></td><td></td><td></td>
                      </tr>
                      <tr>
                        <td className="gv-data-label">NO. OF TURNS</td>
                        <td colSpan="5">12</td>
                        <td colSpan="5">17</td>
                        <td colSpan="5">17</td>
                        <td colSpan="5">24</td>
                        <td colSpan="5">24</td>
                        <td colSpan="5">29</td>
                        <td colSpan="5">35</td>
                        <td colSpan="5"></td>
                        <td colSpan="5"></td>
                        <td colSpan="5"></td>
                        <td colSpan="5"></td>
                        <td colSpan="5"></td>
                      </tr>
                      <tr>
                        <td className="gv-data-label">THRUST (KN)</td>
                        <td>21.1</td><td>28.4</td><td>35.8</td><td>43.1</td><td>51.1</td>
                        <td>32.29</td><td>43.67</td><td>55.9</td><td>68.8</td><td>83.1</td>
                        <td>39.3</td><td>56.3</td><td>74.5</td><td>93.8</td><td>114.5</td>
                        <td></td><td></td><td></td><td></td><td></td>
                        <td></td><td></td><td></td><td></td><td></td>
                        <td></td><td></td><td></td><td></td><td></td>
                        <td></td><td></td><td></td><td></td><td></td>
                        <td></td><td></td><td></td><td></td><td></td>
                        <td></td><td></td><td></td><td></td><td></td>
                        <td></td><td></td><td></td><td></td><td></td>
                        <td></td><td></td><td></td><td></td><td></td>
                        <td></td><td></td><td></td><td></td><td></td>
                      </tr>
                      <tr>
                        <td className="gv-data-label">TORQUE (Nm)</td>
                        <td>52.7</td><td>71.1</td><td>89.4</td><td>107.7</td><td>127.8</td>
                        <td>96.3</td><td>130.2</td><td>165.9</td><td>204.4</td><td>247.2</td>
                        <td>132.5</td><td>189.5</td><td>251.9</td><td>314.9</td><td>384.3</td>
                        <td></td><td></td><td></td><td></td><td></td>
                        <td></td><td></td><td></td><td></td><td></td>
                        <td></td><td></td><td></td><td></td><td></td>
                        <td></td><td></td><td></td><td></td><td></td>
                        <td></td><td></td><td></td><td></td><td></td>
                        <td></td><td></td><td></td><td></td><td></td>
                        <td></td><td></td><td></td><td></td><td></td>
                        <td></td><td></td><td></td><td></td><td></td>
                        <td></td><td></td><td></td><td></td><td></td>
                      </tr>
                      {/* CLASS SIZE 2500 */}
                      <tr className="gv-class-row">
                        <td rowSpan="4" className="gv-class-cell">2500</td>
                        <td className="gv-data-label">PRESSURE RATING (MPa)</td>
                        <td>10</td><td>20</td><td>30</td><td>37</td><td>42.5</td>
                        <td>10</td><td>20</td><td>30</td><td>37</td><td>42.5</td>
                        <td>10</td><td>20</td><td>30</td><td>37</td><td>42.5</td>
                        <td></td><td></td><td></td><td></td><td></td>
                        <td></td><td></td><td></td><td></td><td></td>
                        <td></td><td></td><td></td><td></td><td></td>
                        <td></td><td></td><td></td><td></td><td></td>
                        <td></td><td></td><td></td><td></td><td></td>
                        <td></td><td></td><td></td><td></td><td></td>
                        <td></td><td></td><td></td><td></td><td></td>
                        <td></td><td></td><td></td><td></td><td></td>
                        <td></td><td></td><td></td><td></td><td></td>
                        <td></td><td></td><td></td><td></td><td></td>
                      </tr>
                      <tr>
                        <td className="gv-data-label">NO. OF TURNS</td>
                        <td colSpan="5">10</td>
                        <td colSpan="5">14</td>
                        <td colSpan="5">14</td>
                        <td colSpan="5">20</td>
                        <td colSpan="5">19</td>
                        <td colSpan="5">16</td>
                        <td colSpan="5">19</td>
                        <td colSpan="5"></td>
                        <td colSpan="5"></td>
                        <td colSpan="5"></td>
                        <td colSpan="5"></td>
                        <td colSpan="5"></td>
                      </tr>
                      <tr>
                        <td className="gv-data-label">THRUST (KN)</td>
                        <td>28</td><td>41.1</td><td>54.2</td><td>63.4</td><td>70.6</td>
                        <td>43.6</td><td>64.4</td><td>85.4</td><td>99.9</td><td>111.45</td>
                        <td>54.8</td><td>82.8</td><td>110.9</td><td>130.4</td><td>145.8</td>
                        <td></td><td></td><td></td><td></td><td></td>
                        <td></td><td></td><td></td><td></td><td></td>
                        <td></td><td></td><td></td><td></td><td></td>
                        <td></td><td></td><td></td><td></td><td></td>
                        <td></td><td></td><td></td><td></td><td></td>
                        <td></td><td></td><td></td><td></td><td></td>
                        <td></td><td></td><td></td><td></td><td></td>
                        <td></td><td></td><td></td><td></td><td></td>
                        <td></td><td></td><td></td><td></td><td></td>
                      </tr>
                      <tr>
                        <td className="gv-data-label">TORQUE (Nm)</td>
                        <td>70</td><td>102.5</td><td>135.3</td><td>158.2</td><td>176.2</td>
                        <td>130.1</td><td>192</td><td>254.2</td><td>297.5</td><td>331.5</td>
                        <td>184.2</td><td>278.3</td><td>372.4</td><td>438.2</td><td>490.1</td>
                        <td></td><td></td><td></td><td></td><td></td>
                        <td></td><td></td><td></td><td></td><td></td>
                        <td></td><td></td><td></td><td></td><td></td>
                        <td></td><td></td><td></td><td></td><td></td>
                        <td></td><td></td><td></td><td></td><td></td>
                        <td></td><td></td><td></td><td></td><td></td>
                        <td></td><td></td><td></td><td></td><td></td>
                        <td></td><td></td><td></td><td></td><td></td>
                        <td></td><td></td><td></td><td></td><td></td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
                </div>
            </div>
          </div>
        </section>
      </div>

     

     
        

      

      

     

      <InquiryBanner />
      <Footer />
      {scrollY > 300 && (
        <button 
          className="scroll-to-top"
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          style={{
            position: 'fixed',
            bottom: '2rem',
            right: '2rem',
            background: '#dc2626',
            color: 'white',
            border: 'none',
            borderRadius: '50%',
            width: '50px',
            height: '50px',
            cursor: 'pointer',
            fontSize: '1.2rem',
            zIndex: 1000,
            boxShadow: '0 4px 12px rgba(220, 38, 38, 0.3)',
            transition: 'all 0.3s ease'
          }}
        >
          ↑
        </button>
      )}
    </>
  );
};

export default GateValves;

